import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Form, InputNumber, Button } from 'antd';
import { formatToTL, hasRole } from '../../utils';

const { Text } = Typography;

interface DiscountModalProps {
  visible: boolean;
  totalPrice: number | null;
  existingDiscountAmount?: number | null;
  onCancel: () => void;
  onSubmit: (params: { discountAmount: number; newTotalPrice: number }) => void;
  submitButtonText?: string;
  title?: string;
  disableSubmit?: boolean;
  submitLoading?: boolean;
  maxDiscountRate?: number | null;
}

const DiscountModal: React.FC<DiscountModalProps> = ({
  visible,
  totalPrice,
  existingDiscountAmount = null,
  onCancel,
  onSubmit,
  submitButtonText = 'İndirimli Teklifi Oluştur',
  title = 'İndirim Uygula',
  disableSubmit = false,
  submitLoading = false,
  maxDiscountRate = null,
}) => {
  const [form] = Form.useForm();
  const [hasFormError, setHasFormError] = useState(false);
  const [discountAmount, setDiscountAmount] = useState<number | null>(null);
  const normalizedOriginal = useMemo(() => totalPrice ?? 0, [totalPrice]);
  const appliedDiscount = useMemo(
    () => Math.max(0, existingDiscountAmount ?? 0),
    [existingDiscountAmount]
  );
  const remainingAmount = useMemo(
    () => Math.max(0, normalizedOriginal - appliedDiscount),
    [normalizedOriginal, appliedDiscount]
  );
  const [discountedPrice, setDiscountedPrice] = useState<number>(remainingAmount);

  const isUserAdmin = hasRole('ADMIN');
  const nonAdminRate = useMemo(() => {
    if (typeof maxDiscountRate !== 'number' || !Number.isFinite(maxDiscountRate)) {
      return 0;
    }
    if (maxDiscountRate < 0 || maxDiscountRate > 100) {
      return 0;
    }
    return maxDiscountRate;
  }, [maxDiscountRate]);

  const hasConfiguredNonAdminRate = useMemo(
    () =>
      typeof maxDiscountRate === 'number' &&
      Number.isFinite(maxDiscountRate) &&
      maxDiscountRate >= 0 &&
      maxDiscountRate <= 100,
    [maxDiscountRate]
  );

  const maxTotalDiscountForNonAdmin = useMemo(
    () => (normalizedOriginal * nonAdminRate) / 100,
    [normalizedOriginal, nonAdminRate]
  );

  const remainingDiscountAllowance = useMemo(
    () => Math.max(0, maxTotalDiscountForNonAdmin - appliedDiscount),
    [appliedDiscount, maxTotalDiscountForNonAdmin]
  );

  const maxDiscountAmount = useMemo(() => {
    if (!remainingAmount) return 0;
    if (isUserAdmin) {
      return Math.max(0, Math.floor(remainingAmount));
    }

    const rawMax = Math.min(remainingAmount, remainingDiscountAllowance);
    return Math.max(0, Math.floor(rawMax));
  }, [isUserAdmin, remainingAmount, remainingDiscountAllowance]);

  const discountRateLabel = useMemo(() => {
    if (isUserAdmin) {
      return null;
    }
    if (!hasConfiguredNonAdminRate) {
      return 0;
    }
    const cappedRate = nonAdminRate;
    return Number.isInteger(cappedRate) ? cappedRate : Number(cappedRate.toFixed(2));
  }, [hasConfiguredNonAdminRate, isUserAdmin, nonAdminRate]);

  const discountLabel = useMemo(
    () =>
      discountRateLabel === null
        ? `İndirim tutarını belirleyin (Maksimum: ${formatToTL(maxDiscountAmount)})`
        : `İndirim tutarını belirleyin (Maksimum: %${discountRateLabel} / ${formatToTL(maxDiscountAmount)})`,
    [discountRateLabel, maxDiscountAmount]
  );

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setHasFormError(false);
    setDiscountAmount(null);
    setDiscountedPrice(remainingAmount);
  }, [visible, remainingAmount, form]);

  useEffect(() => {
    if (!visible) return;
    setDiscountedPrice(Math.max(remainingAmount - (discountAmount ?? 0), 0));
  }, [discountAmount, remainingAmount, visible]);

  const handleFieldsChange = () => {
    const errors = form.getFieldsError();
    setHasFormError(errors.some((field) => field.errors.length > 0));
  };

  const handleDiscountChange = (value: number | null) => {
    setDiscountAmount(value ?? null);
  };

  const displayedPrice = useMemo(() => formatToTL(discountedPrice), [discountedPrice]);

  const isSubmitDisabled =
    submitLoading || disableSubmit || hasFormError || remainingAmount === 0 || discountedPrice === remainingAmount;

  const handleSubmit = () => {
    form
      .validateFields()
      .then(() => {
        const safeDiscount = typeof discountAmount === 'number' ? discountAmount : 0;
        const newTotalPrice = Math.max(remainingAmount - safeDiscount, 0);
        onSubmit({ discountAmount: safeDiscount, newTotalPrice });
      })
      .catch((err: any) => {
        console.log('Form error:', err);
      });
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={submitLoading ? undefined : onCancel}
      footer={null}
      closable={!submitLoading}
      maskClosable={!submitLoading}
    >
      <>
        <p>İndirimli fiyat teklifinizi hızlı bir şekilde hazırlayın.</p>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Teklif Fiyatı</Text>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{formatToTL(remainingAmount)}</div>
        </div>

        <Form layout="vertical" form={form} onFieldsChange={handleFieldsChange}>
          <Form.Item
            label={discountLabel}
            name="discountAmount"
            rules={[
              {
                validator: (_: any, value: number | null) => {
                  if (value === undefined || value === null) {
                    return Promise.resolve();
                  }
                  if (typeof value === 'number' && value >= 0 && value <= maxDiscountAmount) {
                    return Promise.resolve();
                  }
                  if (maxDiscountAmount === 0) {
                    return Promise.reject(new Error('Daha fazla indirim uygulayamazsınız!'));
                  }
                  return Promise.reject(
                    new Error(
                      `İndirim tutarı 0 ile ${formatToTL(maxDiscountAmount)} arasında olmalıdır!`
                    )
                  );
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              step={1}
              precision={0}
              style={{ width: '100%' }}
              placeholder="İndirim Tutarı Giriniz"
              className="d-input d-input-form d-flex align-items-center"
              onKeyDown={(event) => {
                if (event.key === ',') {
                  event.preventDefault();
                }
              }}
              onChange={handleDiscountChange}
            />
          </Form.Item>

          <div style={{ marginTop: 16 }}>
            <Text strong>Yeni Teklif Tutarı</Text>
            <div
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#52c41a',
                marginTop: '8px',
              }}
            >
              {displayedPrice}
            </div>
          </div>
        </Form>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Button
            type="primary"
            className="d-button dark-button width-auto p-20px"
            disabled={isSubmitDisabled}
            loading={submitLoading}
            onClick={handleSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      </>
    </Modal>
  );
};

export default DiscountModal;
